import { X } from "lucide-react";
import { useEffect, useState } from "react";
import UsersApi from "../../../api/usersApi";
import CircleAvatar from "../../../components/CircleAvatar/CircleAvatar";
import Input from "../../../components/Input/Input";
import { TypeUserData } from "../../../types/TypeUsers";
import s from "./HubManagement.module.css";
import { getUserLabel } from "./hubManagementUtils";

type UserSearchSelectProps = {
  label: string;
  selectedUser?: TypeUserData | null;
  onSelect: (user: TypeUserData) => void;
  onClear: () => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
};

const UserSearchSelect = ({
  label,
  selectedUser,
  onSelect,
  onClear,
  placeholder = "Search by name or email...",
  required = false,
  disabled = false,
}: UserSearchSelectProps) => {
  const token = localStorage.getItem("token");
  const [search, setSearch] = useState("");
  const [users, setUsers] = useState<TypeUserData[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  useEffect(() => {
    if (selectedUser?._id) {
      setSearch("");
      setUsers([]);
    }
  }, [selectedUser?._id]);

  useEffect(() => {
    let isCancelled = false;

    if (!search.trim() || !token || disabled) {
      setUsers([]);
      setIsSearching(false);
      return;
    }

    const searchUsers = async () => {
      setIsSearching(true);
      try {
        const response = await UsersApi.findUserBySearchString(
          token,
          search.trim(),
          "false",
        );
        if (!isCancelled) {
          setUsers(response.status && response.users ? response.users : []);
        }
      } catch (error) {
        if (!isCancelled) {
          console.error("Error searching users:", error);
          setUsers([]);
        }
      } finally {
        if (!isCancelled) {
          setIsSearching(false);
        }
      }
    };

    searchUsers();

    return () => {
      isCancelled = true;
    };
  }, [disabled, search, token]);

  return (
    <div className={s.userSearchBlock}>
      <span className={s.fieldLabel}>
        {label}
        {required ? " *" : ""}
      </span>
      {selectedUser?._id ? (
        <div className={s.selectedUserBlock}>
          <div className={s.selectedUser}>
            <CircleAvatar
              userId={selectedUser._id}
              width="36px"
              height="36px"
              fontSize="14px"
            />
            <div className={s.selectedUserText}>
              <span className={s.userName}>{getUserLabel(selectedUser)}</span>
              {selectedUser.email && <small>{selectedUser.email}</small>}
            </div>
          </div>
          <button
            type="button"
            className={s.iconButton}
            onClick={onClear}
            aria-label={`Clear ${label}`}
            disabled={disabled}
          >
            <X size={16} />
          </button>
        </div>
      ) : (
        <>
          <Input
            inputValue={search}
            onChangeInput={setSearch}
            placeholder={placeholder}
            required={required}
            isSearchinput
            bgColor="rgba(255, 255, 255, 0.06)"
            border="1px solid rgba(255, 255, 255, 0.12)"
            padding="10px 44px 10px 20px"
            disabled={disabled}
          />
          {(users.length > 0 || isSearching) && (
            <div className={s.userResults}>
              {isSearching ? (
                <div className={s.userResultMuted}>Searching...</div>
              ) : (
                users.map((user) => (
                  <button
                    key={user._id}
                    type="button"
                    className={s.userResult}
                    onClick={() => onSelect(user)}
                  >
                    <CircleAvatar
                      userId={user._id}
                      width="32px"
                      height="32px"
                      fontSize="14px"
                    />
                    <span className={s.userName}>{getUserLabel(user)}</span>
                    {user.email && <small>{user.email}</small>}
                  </button>
                ))
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UserSearchSelect;
